/**
 * Hook for managing global filter state
 *
 * Well-level filtering is supported but shows a one-time warning since permits
 * are at the aggregate system level. When filtering by wells, totals show
 * partial production against the full system permit allocation.
 */

import { useState, useEffect, useMemo, useCallback } from "react";

/**
 * Manage global filter state for production report
 * @param {Object} filterOptions - Available filter options
 * @returns {Object} Filter state and handlers
 */
export const useGlobalFilters = (filterOptions) => {
  const [selectedYears, setSelectedYears] = useState([]);
  const [selectedAquifers, setSelectedAquifers] = useState([]);
  const [selectedAggSystems, setSelectedAggSystems] = useState([]);
  const [selectedWells, setSelectedWells] = useState([]);
  const [filtersInitialized, setFiltersInitialized] = useState(false);

  // Initialize filters to default values only once when data first loads
  useEffect(() => {
    if (!filtersInitialized && filterOptions.years.length > 0) {
      // Default to the highest year in the list
      const maxYear = Math.max(...filterOptions.years);
      setSelectedYears([maxYear]);
      setSelectedAquifers([...filterOptions.aquifers]);
      setSelectedAggSystems([...filterOptions.aggSystems]);
      setSelectedWells([...filterOptions.wells]);
      setFiltersInitialized(true);
    }
  }, [filterOptions, filtersInitialized]);

  // Create lookup sets for efficient filtering
  const selectedYearsSet = useMemo(
    () => new Set(selectedYears),
    [selectedYears]
  );
  const selectedAquifersSet = useMemo(
    () => new Set(selectedAquifers.map((a) => a.ndx)),
    [selectedAquifers]
  );
  const selectedAggSystemsSet = useMemo(
    () => new Set(selectedAggSystems.map((a) => a.ndx)),
    [selectedAggSystems]
  );
  const selectedWellsSet = useMemo(
    () => new Set(selectedWells.map((w) => w.ndx)),
    [selectedWells]
  );
  // Check if "No Permit" option (ndx=null) is selected
  const includeNoPermit = useMemo(
    () => selectedAggSystems.some((a) => a.ndx === null),
    [selectedAggSystems]
  );
  // Check if well filter is active (not all wells selected)
  const isWellFilterActive = useMemo(
    () =>
      filtersInitialized &&
      selectedWells.length > 0 &&
      selectedWells.length < filterOptions.wells.length,
    [filtersInitialized, selectedWells, filterOptions.wells]
  );

  const handleResetFilters = useCallback(() => {
    // Default to the highest year in the list
    const maxYear = Math.max(...filterOptions.years);
    setSelectedYears([maxYear]);
    setSelectedAquifers([...filterOptions.aquifers]);
    setSelectedAggSystems([...filterOptions.aggSystems]);
    setSelectedWells([...filterOptions.wells]);
  }, [filterOptions]);

  return {
    // Selected values
    selectedYears,
    selectedAquifers,
    selectedAggSystems,
    selectedWells,
    // Setters
    setSelectedYears,
    setSelectedAquifers,
    setSelectedAggSystems,
    setSelectedWells,
    // Sets for efficient lookup
    selectedYearsSet,
    selectedAquifersSet,
    selectedAggSystemsSet,
    selectedWellsSet,
    includeNoPermit,
    isWellFilterActive,
    // State
    filtersInitialized,
    // Handlers
    handleResetFilters,
  };
};
